//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import {
  CuralateCatalogProduct,
  CuralateProductImage,
} from '@apis/universal-image-actions/universalImageActionsApiModel';
import { OverlayIconButton } from '@components/atoms/button/Button';
import { ImageWithErrorHandling } from '@components/atoms/image-with-error-handling/ImageWithErrorHandling';
import productImagePlaceholder from '@images/acorn-broken.svg';
import { Product } from '@models/IItemMetadata';
import { UiaProductTrackingIds } from '@uia/products/constants/UiaProductConstants';
import { getProductOrTagImageUrls } from '@uia/products/utils';

interface Props {
  product: Product | CuralateCatalogProduct;
  selectedImageIndex: number;
  setSelectedImageIndex: (index: number) => void;
  uploadedImage?: CuralateProductImage;
}

export const ProductImageCarousel = ({
  product,
  selectedImageIndex,
  setSelectedImageIndex,
  uploadedImage,
}: Props) => {
  const imageUrls = getProductOrTagImageUrls(product, uploadedImage);

  return (
    <CarouselContainer>
      <ProductImage
        alt={product.name ?? 'Product image'}
        src={imageUrls[selectedImageIndex] ?? productImagePlaceholder}
      />
      {imageUrls.length > 1 && (
        <>
          <PreviousImageButton
            isDisabled={selectedImageIndex === 0}
            onClick={() => setSelectedImageIndex(selectedImageIndex - 1)}
            trackingId={
              UiaProductTrackingIds.PRODUCT_EDITOR_IMAGE_CAROUSEL_PREV_BUTTON
            }
          />
          <NextImageButton
            isDisabled={selectedImageIndex === imageUrls.length - 1}
            onClick={() => setSelectedImageIndex(selectedImageIndex + 1)}
            trackingId={
              UiaProductTrackingIds.PRODUCT_EDITOR_IMAGE_CAROUSEL_NEXT_BUTTON
            }
          />
        </>
      )}
    </CarouselContainer>
  );
};

const CarouselContainer = styled.div`
  position: relative;
  top: 0;
  left: 0;
`;

const CarouselButton = styled(OverlayIconButton)(
  ({ theme }) => `
  position: absolute;
  border-radius: ${theme.borderRadii[0]};
  width: ${theme.spacings[3]};
  min-width: ${theme.spacings[3]};
  height: ${theme.spacings[3]};
  top: ${theme.spacings[2]};
  & > button {
    padding: ${theme.spacings[0]};
  }
`,
);

const PreviousImageButton = styled(CarouselButton).attrs({
  iconName: 'chevron-left',
})(
  ({ theme }) => `
    left: ${theme.spacings[2]};
`,
);

const NextImageButton = styled(CarouselButton).attrs({
  iconName: 'chevron-right',
})(
  ({ theme }) => `
    right: ${theme.spacings[2]};
  `,
);

const ProductImage = styled(ImageWithErrorHandling)`
  position: relative;
  aspect-ratio: 1;
  object-fit: contain;
  width: 120px;
  top: 0;
  left: 0;
`;
